import React, { useState } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { getI18n, useTranslation } from 'react-i18next';
import { Select, Typography } from 'antd';
import styled from 'styled-components';

import TableHeader from '~/pages/agent/components/agent/tableElements/TableHeader';
import { useSalesUsers, useEditSalesLead } from '~/controllers/sales';
import { DirectionProp } from '~/types/Direction';
import direction from '~/utils/direction';
import { useDirection } from '~/hooks/i18nHooks';
import { queryClient } from '~/index';
import { getRoles } from '~/utils/getDecodedJwt';

function salesAssigneeCell(visibleColumns: any) {
  if (!visibleColumns.includes('assignee')) {
    return null;
  }
  return {
    title: () => (
      <TableHeader title={getI18n().t('Assignee', { ns: 'sales' })} sortingDataKey="assignee" />
    ),
    dataIndex: ['assignee'],
    key: 'assignee',
    render: (assignee: any, record: any) => (
      <AssigneeDropdown
        currentAssignee={assignee?.fullName}
        currentAssigneeId={assignee?.id}
        record={record}
      />
    ),
    width: 150,
  };
}

interface Props {
  currentAssignee?: string;
  currentAssigneeId?: string;
  record?: any;
  disabled?: boolean;
}

const AssigneeDropdown = (props: Props) => {
  const { currentAssignee, currentAssigneeId, record, disabled } = props;
  const salesUsers = useSalesUsers();
  const editLead = useEditSalesLead();
  const dir = useDirection();
  const { t } = useTranslation('');

  const options = salesUsers.data?.map(({ id, fullName }) => ({
    label: fullName,
    value: id,
    key: id,
  }));

  const onSelect = (salesId: string) => {
    if (record) {
      const selectedUser = salesUsers.data?.find(user => user.id === salesId);
      if (selectedUser) {
        const updatedLead = {
          ...record,
          assignee: {
            fullName: selectedUser.fullName,
            id: selectedUser.id,
          },
        };

        editLead.mutateAsync(updatedLead).then(() => {
          queryClient.invalidateQueries('my-leads');
        });
      }
    }
  };

  // Check if user is sales-Manager
  const isSalesManager = getRoles().includes('sales-Manager');

  return (
    <Container dir={dir}>
      {isSalesManager ? (
        <Select
          className="salesLeadTable-Assignee"
          placeholder={t('Unassignee', { ns: 'sales' })}
          value={currentAssignee || undefined}
          onChange={onSelect}
          options={options}
          loading={salesUsers.isLoading}
          disabled={disabled || editLead.isLoading}
          style={{ width: '100%' }}
          showSearch
          filterOption={(input, option) =>
            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
      ) : (
        <Typography.Paragraph style={{ margin: 0 }}>
          {currentAssignee || t('-', { ns: 'sales' })}
        </Typography.Paragraph>
      )}
    </Container>
  );
};

const Container = styled.div<DirectionProp>`
  display: flex;
  flex-direction: column;

  .salesLeadTable-Assignee {
    padding: 5px 0;
  }

  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    border: 0;
    background-color: transparent;
    box-shadow: 0 0 0;
  }

  .ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    border: 0;
    box-shadow: 0 0 0;
  }

  box-shadow: transparent;

  .ant-select-selection-item {
    ${direction.padding.right('-18px !important')};
    ${direction.padding.left('0px !important')};
  }

  .ant-select-selection-search {
    ${direction.left('-15px !important')};
    ${direction.right('unset !important')};
  }

  .ant-select-arrow {
    ${direction.right('unset')};
    ${direction.left('-11px')};
  }
`;

export default salesAssigneeCell;
